import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Theme,
  Typography
} from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import {makeStyles} from '@material-ui/styles';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import useReactRouter from 'use-react-router';
import validate from 'validate.js';
import ConfirmDelete from '../../../components/ConfirmDelete';
import InfoPopover from '../../../components/InfoPopover';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {DataTypes} from '../../../graphql/common.datatypes';
import {ResourceOption, ResourceOptionActions} from '../../../graphql/resources.datatypes';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import {isValidDate} from '../../../utils/isValidDate';
import {numberRegEx} from '../../../utils/validators';
import getResourceOption from '../getResourceOption.api';
import updateResource, {UpdateResourceVars} from '../ResourceEdit/updateResource.api';
import cloneResourceOption from './cloneResourceOption.api';
import ResourceOptionConditionEdit, {getBlankResourceOptionCondition} from './ResourceOptionConditionEdit';
import updateResourceOption, {UpdateResourceOptionVars} from './updateResourceOption.api';
import ConfirmModal from '../../../components/ConfirmModal';
import anyLoading from '../../../utils/anyLoading';
import {formatGraphQlDate} from '../../../utils/formatters';
import {checkRole as checkMyRole} from '../../../components/Can/Can';
import {GroupEditingLevelTypes} from '../../../graphql/groups.datatypes';
import updateResourceOptionCascade from './updateResourceOptionCascade.api';
import deleteResourceOption from './deleteResourceOption.api';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  container: {
    //marginTop: theme.spacing(3),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: theme.palette.error.light,
    '&:hover': {
      backgroundColor: theme.palette.error.main
    }
  },
  cloneButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: colors.green[400],
    '&:hover': {
      backgroundColor: colors.green[700]
    }
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  marginBottom: {
    marginBottom: theme.spacing(3),
  },
  marginTop: {
    marginTop: theme.spacing(1),
  },
}));

export const getBlankResourceOption = (parentId: number) => {
  return {
    __typename: 'ResourceOption',
    id: -1,
    resource_id: parentId,
    internal_name: 'New Resource Option',
    display_name: 'n/a',
    description: '',
    disabled: true,
    target: 0,
    action_type: 0,
    action_value: null,
    hidden: false,
    additional_charge: false,

    conditions: [],
  };
};

const validateSchema = {
  internal_name: {
    length: {maximum: 50},
    presence: {allowEmpty: false, message: 'is required'},
  },
  // description: {
  //   presence: {allowEmpty: false, message: 'is required'},
  // },
};

const ResourceOptionEditPure = (props: {
  resourceOption: ResourceOption,
  onResourceOptionDeleted: (id: number) => void,
  onResourceOptionCreated: (resourceOption: ResourceOption) => void,
}) => {
  const classes = useStyles();
  const newResourceOption = props.resourceOption.id === -1;
  const [open, setOpen] = React.useState(false);
  const {enqueueSnackbar} = useSnackbar();
  const [sendUpdateResourceOption, updateOptionData] = updateResourceOption();
  const [sendDeleteResourceOption, deleteOptionData] = deleteResourceOption();
  const [sendUpdateResourceOptionCascade, updateOptionCascadeData] = updateResourceOptionCascade();
  const [sendUpdateResource, updateData] = updateResource();
  const [sendCloneResourceOption, cloneData] = cloneResourceOption();
  const [openUpdateOptions, setOpenUpdateOptions] = React.useState(false);
  const [hasActiveOptions, setHasActiveOptions] = useState(false);
  const loadingData = anyLoading(updateOptionData, updateData, cloneData, updateOptionCascadeData, deleteOptionData);

  const isSuperAdmin = checkMyRole('super-admin');

  const checkExistingOptionValues = () => {
    let optionValueExists = false;

    if (props.resourceOption.groupResources && props.resourceOption.groupResources.length > 0) {
      optionValueExists = true;
    }

    return optionValueExists;
  }

  //1. Set form and error state
  const [formState, setFormState] = useState<ResourceOption>({...props.resourceOption});
  const [deleteIds, setDeleteIds] = useState<number[]>([]);
  const actionValue = ResourceOptionActions[Number(formState.action_type)].value;

  const [errors, setErrors] = useState({
    isValid: false,
    errors: {
      display_name: false,
      internal_name: false,
      description: false,
      disabled: false,
      hidden: false,
      additional_charge: false,
      target: false,
      conditions: false,
      action_type: false,
      action_value: false,
    }
  });

  //2. Setup helper functions for form
  const handleFieldChange = getHandleFieldChange(setFormState);
  const hasError = getHasError(errors);

  const getValueAsString = (value: any): string => {
    if (isValidDate(value)) return formatGraphQlDate(value)!;
    return value;
  };

  //3. Chcek form on updates
  useEffect(() => {
    const newErrors = validate(formState, validateSchema);

    setErrors(state => ({
      ...state,
      isValid: !newErrors,
      errors: newErrors || {}
    }));
  }, [formState]);

  useEffect(() => {
    let newValue: string | null = null;
    if (!actionValue && formState.action_value) newValue = '';
    if (actionValue && actionValue.type === DataTypes.NUMBER && !numberRegEx.test(String(formState.action_value!))) newValue = actionValue.defaultValue;
    if (newValue !== null) setFormState(oldState => {
      return {...oldState, action_value: newValue};
    });
  }, [formState.action_type]);

  useEffect(() => {
    setFormState({...props.resourceOption});
    setHasActiveOptions(checkExistingOptionValues());
  }, [props.resourceOption]);

  //4. Handle save form
  const onSaveClick = () => {
    if (hasActiveOptions) {
      setOpenUpdateOptions(true);
    } else {
      executeSave();
    }
  };

  const executeSave = (updateOptions: boolean = false) => {
    if (newResourceOption) {
      const sanitisedData: UpdateResourceVars = {
        data: {
          ...{
            id: formState.resource_id!,
            options: {
              create: [{
                internal_name: formState.internal_name!,
                display_name: formState.display_name!,
                description: formState.internal_name!,
                disabled: true, //formState.disabled!,
                hidden: formState.hidden!,
                additional_charge: formState.additional_charge!,
                target: formState.target!,
                action_type: formState.action_type!,
                action_value: formState.action_value ? formState.action_value : null,
                conditions: undefined,
              }]
            }
          },
        }
      };

      if (formState.conditions) {
        const createConditions = formState.conditions
            .filter(item => item.id === -1)
            .map(item => ({
              __typename: undefined,
              id: undefined,
              type: item.type!,
              attribute: item.attribute!,
              attribute_type: item.attribute_type!,
              attribute_value: getValueAsString(item.attribute_value!),
              condition: item.condition!,
            }));

        sanitisedData.data.options!.create![0].conditions = {
          create: createConditions,
        };
      }

      sendUpdateResource({variables: sanitisedData})
          .then((response) => {
            enqueueSnackbar('Resource Saved.', {variant: 'success'});

            if (props.onResourceOptionCreated) {
              props.onResourceOptionCreated([...response.data!.updateResource!.options!].pop() as ResourceOption);
            }
          })
          .catch(() => {
            enqueueSnackbar('Save Resource Failed!', {variant: 'error'});
          });
    } else {
      const sanitisedData: UpdateResourceOptionVars = {
        data: {
          ...{
            ...formState,
            id: formState.id!,
            __typename: undefined,
            groupResources: undefined,
            display_name: formState.display_name!,
            description: formState.internal_name!,
            conditions: {
              delete: deleteIds,
            },
            resource_id: undefined,
          },
        }
      };

      if (formState.conditions) {
        const createConditions = formState.conditions
            .filter(item => item.id === -1)
            .map(item => ({
              type: item.type!,
              attribute: item.attribute!,
              attribute_type: item.attribute_type!,
              attribute_value: getValueAsString(item.attribute_value!),
              condition: item.condition!,
            }));

        const updateConditions = formState.conditions
            .filter(item => item.id !== -1)
            .map(item => ({
              id: item.id!,
              type: item.type!,
              attribute: item.attribute!,
              attribute_type: item.attribute_type!,
              attribute_value: getValueAsString(item.attribute_value!),
              condition: item.condition,
            }));

        sanitisedData.data.conditions!.create = createConditions;
        sanitisedData.data.conditions!.update = updateConditions;
      }

      sendUpdateResourceOption({variables: sanitisedData})
          .then(() => {
            enqueueSnackbar('Resource Option Updated.', {variant: 'success'});

            if (updateOptions) {
              sendUpdateResourceOptionCascade({variables: sanitisedData})
                  .then(() => {
                    enqueueSnackbar('Resource Option Cascade Updated.', {variant: 'success'});
                  })
                  .catch(() => {
                    enqueueSnackbar('Resource Option Cascade Update Failed!', {variant: 'error'});
                  });
            }

          })
          .catch(() => {
            enqueueSnackbar('Resource Option Update Failed!', {variant: 'error'});
          });
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleDelete = () => {
    sendDeleteResourceOption({variables: {id: formState.id!}})
      .then(() => {
        enqueueSnackbar('Resource Option Deleted.', {variant: 'success'});
        props.onResourceOptionDeleted(formState.id!);
      })
      .catch(() => {
        enqueueSnackbar('Delete Resource Option Failed!', {variant: 'error'});
      });

    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAddCondition = () => {
    setFormState(oldState => {
      const newCondition = getBlankResourceOptionCondition(oldState.id!);
      const conditions = oldState.conditions ? [...oldState.conditions, newCondition] : [newCondition];
      return {...oldState, conditions};
    })
  };

  const handleDeleteCondition = (index: number) => {
    const deleteId = formState.conditions![index].id!;
    if (deleteId > -1) setDeleteIds([...deleteIds, deleteId]);

    const conditions = formState.conditions ? [...formState.conditions.filter((item, i) => i !== index)] : [];
    setFormState(oldState => {
      return {...oldState, conditions};
    });
  };

  const onCloneClick = () => {
    sendCloneResourceOption({variables: {id: props.resourceOption.id!}})
        .then((response) => {
          enqueueSnackbar('Resource Option Cloned.', {variant: 'success'});
          const newItem = response.data!.cloneResourceOption as ResourceOption;
          if (props.onResourceOptionCreated) {
            props.onResourceOptionCreated(newItem);
          }
        })
        .catch(() => {
          enqueueSnackbar('Resource Option Clone Failed!', {variant: 'error'});
        });
  };

  const handleCloseUpdateOptions = () => {
    setOpenUpdateOptions(false);
  };

  return (
      <React.Fragment>
        <LoadingOverlay loading={loadingData.loading}>
          <Card className={classes.card}>
            <CardHeader title="Details"/>
            <CardContent>

              <Grid container spacing={3}>

                <Grid item md={12} xs={12}>
                  <TextField
                      {...hasError('internal_name')}
                      fullWidth
                      label={'Internal Name'}
                      name="internal_name"
                      onChange={handleFieldChange}
                      value={formState.internal_name}
                      variant="outlined"
                      disabled={!isSuperAdmin}
                  />
                </Grid>

                <Grid item md={12} xs={12}>
                  <TextField
                      {...hasError('display_name')}
                      fullWidth
                      label={'Display Name'}
                      name="display_name"
                      onChange={handleFieldChange}
                      value={formState.display_name}
                      variant="outlined"
                      disabled={!isSuperAdmin}
                  />
                </Grid>


                <Grid item xs={12}>
                  <TextField
                      {...hasError('view_level')}
                      label="View Level Permissions"
                      fullWidth
                      onChange={handleFieldChange}
                      select
                      name="view_level"
                      // eslint-disable-next-line react/jsx-sort-props
                      SelectProps={{native: true}}
                      value={formState.view_level}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="outlined"
                  >
                    {GroupEditingLevelTypes.map((type, i) => (
                        <option key={type.name} value={i}>
                          {type.name}
                        </option>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                      {...hasError('editing_level')}
                      label="Editing Level Permissions"
                      fullWidth
                      onChange={handleFieldChange}
                      select
                      name="editing_level"
                      // eslint-disable-next-line react/jsx-sort-props
                      SelectProps={{native: true}}
                      value={formState.editing_level}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="outlined"
                  >
                    {GroupEditingLevelTypes.map((type, i) => (
                        <option key={type.name} value={i}>
                          {type.name}
                        </option>
                    ))}
                  </TextField>
                </Grid>

          </Grid>

          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>
              <Switch
                checked={formState.disabled}
                color="secondary"
                edge="start"
                name="disabled"
                onChange={handleFieldChange}
                value={formState.disabled}
                disabled={!isSuperAdmin || newResourceOption}
              />
            </Grid>
            <Grid item><Typography variant="h5">Disabled?</Typography></Grid>
          </Grid>

          <Grid component="label" container alignItems="center" spacing={1} >
            <Grid item>
              <Switch
                checked={formState.hidden}
                color="secondary"
                edge="start"
                name="hidden"
                onChange={handleFieldChange}
                value={formState.hidden}
                disabled={!isSuperAdmin}
              />
            </Grid>
            <Grid item><Typography variant="h5">Hide on Booking Form and Invoice?</Typography></Grid>
          </Grid>

          <Grid component="label" container alignItems="center" spacing={1} >
            <Grid item>
              <Switch
                  checked={formState.additional_charge}
                  color="secondary"
                  edge="start"
                  name="additional_charge"
                  onChange={handleFieldChange}
                  value={formState.additional_charge}
                  disabled={!isSuperAdmin}
              />
            </Grid>
            <Grid item><Typography variant="h5">Show as additional charge on invoice?</Typography></Grid>
          </Grid>

              <Typography variant="body1">
                Options are rules that are used to add Resources to Bookings.
                An Option will check a set of Targets for any matches. These Targets are the DataTypes associated with a
                booking (i.e. Tour, Group, Group Member etc)
                Options will be checked whenever a Booking is updated to look for new matches.
                <br/><br/>
                Example: To give a $30 discount to all children under 3.<br/>
                Create a Resource Bank that is non consumable, no date range.<br/>
                1. Condition Group Member age is less than 3.<br/>
                3. Apply Group Member Resource Mandatory.<br/>
              </Typography>
            </CardContent>
          </Card>

          {/*<Card className={classes.card}>*/}
          {/*  <CardHeader title="1. Option Targets"/>*/}
          {/*  <CardContent>*/}

          {/*    <TextField*/}
          {/*      {...hasError('target')}*/}
          {/*      fullWidth*/}
          {/*      label="Targets"*/}
          {/*      name="target"*/}
          {/*      onChange={handleFieldChange}*/}
          {/*      select*/}
          {/*      // eslint-disable-next-line react/jsx-sort-props*/}
          {/*      SelectProps={{native: true}}*/}
          {/*      value={String(formState.target)}*/}
          {/*      variant="outlined"*/}
          {/*      InputLabelProps={{*/}
          {/*        shrink: true,*/}
          {/*      }}*/}
          {/*      InputProps={{*/}
          {/*        endAdornment: <InputAdornment position="end">*/}
          {/*          <InfoPopover message="All targets of the selected type will be checked by this option whenever a Booking is updated." />*/}
          {/*        </InputAdornment>,*/}
          {/*      }}*/}
          {/*    >*/}
          {/*      {ResourceOptionTargets.map((target, i) => (*/}
          {/*        <option key={i} value={i}>*/}
          {/*          {target.name}*/}
          {/*        </option>*/}
          {/*      ))}*/}
          {/*    </TextField>*/}
          {/*  </CardContent>*/}
          {/*</Card>*/}

          <Card className={classes.card}>
            <CardHeader
                title="1. Match All Conditions:"
                action={(
                    <IconButton style={{color: colors.green[600]}} onClick={handleAddCondition} disabled={!isSuperAdmin}>
                      <AddCircleOutlineIcon/>
                    </IconButton>
                )}
            />
            <CardContent>
              <Typography variant="body1" className={classes.marginBottom}>These Conditions must all be true for the
                Option to be applied.</Typography>
              {formState.conditions && formState.conditions.map((condition, i) => (
                  <ResourceOptionConditionEdit
                      key={i}
                      index={i}
                      condition={condition}
                      handleDelete={() => handleDeleteCondition(i)}
                      setFormState={setFormState}
                      disabled={!isSuperAdmin}
                  />
              ))}
            </CardContent>
          </Card>

          <Card className={classes.card}>
            <CardHeader title="2. Take Action On All Targets That Match:"/>
            <CardContent>
              <TextField
                  {...hasError('action_type')}
                  fullWidth
                  label="Action"
                  name="action_type"
                  onChange={handleFieldChange}
                  select
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{native: true}}
                  value={String(formState.action_type)}
                  variant="outlined"
                  className={classes.marginBottom}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">
                      <InfoPopover
                          message="Once all of the Conditions are true this rule will be applied to all matches in a Booking."/>
                    </InputAdornment>,
                  }}
                  disabled={!isSuperAdmin}
              >
                {ResourceOptionActions.map((action, i) => (
                    <option key={i} value={i}>
                      {action.name}
                    </option>
                ))}
              </TextField>

          {actionValue && actionValue.type === DataTypes.STRING && (
            <TextField
              {...hasError('action_value')}
              fullWidth
              label="Value"
              name="action_value"
              onChange={handleFieldChange}
              variant="outlined"
              value={String(formState.action_value)}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={!isSuperAdmin}
            />
          )}

          {actionValue && actionValue.type === DataTypes.NUMBER && (
            <TextField
              {...hasError('action_value')}
              fullWidth
              label="Value"
              name="action_value"
              type="number"
              onChange={handleFieldChange}
              variant="outlined"
              value={String(formState.action_value)}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={!isSuperAdmin}
            />
          )}

        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardActions>
          <Button className={classes.saveButton} onClick={onSaveClick} variant="contained" disabled={!errors.isValid || !isSuperAdmin}>
            Save
          </Button>
          <Button className={classes.deleteButton} onClick={handleClickOpen} variant="contained"
                  disabled={newResourceOption || !isSuperAdmin}>
            Delete
          </Button>
          <Button className={classes.cloneButton} onClick={onCloneClick} variant="contained"
                  disabled={newResourceOption || !errors.isValid || !isSuperAdmin}>
            Clone
          </Button>
        </CardActions>
      </Card>
        </LoadingOverlay>
        <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>
        <ConfirmModal
            onClose={handleCloseUpdateOptions}
            cancel={'Dont Update Options'}
            onCancelClick={() => executeSave(false)}
            confirm={'Update Options'}
            onConfirmClick={() => executeSave(true)}
            open={openUpdateOptions}
            title={'Cascade Changes'}
            message={'There are users that have this option allocated. Would you like to cascade this update?'}
        />
      </React.Fragment>
  );
};

const ResourceOptionEdit = (props: {
  resourceOptionId: number,
  parentId?: number,
  onResourceOptionDeleted: (id: number) => void,
  onResourceOptionCreated: (resourceOption: ResourceOption) => void,
}) => {
  const {history} = useReactRouter();
  const {resourceOptionId, parentId, ...rest} = props;
  const newResourceOption = resourceOptionId === -1;
  const resourceOptionData = getResourceOption(resourceOptionId);
  const {enqueueSnackbar} = useSnackbar();

  useEffect(() => {
    if (!newResourceOption && resourceOptionData.error) {
      enqueueSnackbar(`There was an error loading Resource Option ${resourceOptionId}`, {variant: 'error'});
      history.push('/resources');
    }
  }, [resourceOptionData.error]);

  return (
    <LoadingOverlay loading={!newResourceOption && resourceOptionData.loading}>
      {!newResourceOption && !resourceOptionData.loading && resourceOptionData.data && resourceOptionData.data.resourceOption && (
        <ResourceOptionEditPure resourceOption={resourceOptionData.data.resourceOption} {...rest} />
      )}
      {newResourceOption && parentId && (
        <ResourceOptionEditPure resourceOption={getBlankResourceOption(parentId)} {...rest} />
      )}
    </LoadingOverlay>
  );
};

export default ResourceOptionEdit;